import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import './SelectorDeFechas.css';

/**
 * Calendario de un mes para elegir fechas, en dos modos: días sueltos (el clic
 * suma o saca) y rango (el primer clic marca el inicio, el segundo el fin).
 *
 * Reemplaza a BlockingCalendar, que tenía tres problemas:
 *
 * 1. Armaba la clave de la fecha con `date.toISOString().split('T')[0]`, que
 *    pasa por UTC. Los turnos guardan un YYYY-MM-DD local, así que en cualquier
 *    zona al este de Greenwich el día se corría uno.
 * 2. Escondía los días anteriores a hoy en vez de deshabilitarlos, y por eso
 *    tenía que calcular los huecos de alineación a partir del primer día que
 *    quedaba. Acá el mes se dibuja entero y los días pasados van apagados.
 * 3. Sus clases (`calendar-header`, `day-number`, `calendar-day-header`) son las
 *    mismas que usan BookingCalendar, PricingCalendar y Dashboard.css. Las de
 *    acá van prefijadas.
 */

const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

/* Con los getters locales, no con `toISOString()`: ver el punto 1 de arriba. */
export const claveFecha = (fecha) =>
  `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(
    fecha.getDate()
  ).padStart(2, '0')}`;

export const formatearFechaCorta = (clave) =>
  new Date(`${clave}T00:00:00`).toLocaleDateString('es-AR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

export default function SelectorDeFechas({
  modo,
  fechas = [],
  rango = { desde: '', hasta: '' },
  onCambiarFechas,
  onCambiarRango,
}) {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const claveHoy = claveFecha(hoy);

  const [mes, setMes] = useState(hoy.getMonth());
  const [anio, setAnio] = useState(hoy.getFullYear());

  const enElMesActual = mes === hoy.getMonth() && anio === hoy.getFullYear();

  const mover = (paso) => {
    const nuevo = new Date(anio, mes + paso, 1);
    if (paso < 0 && nuevo < new Date(hoy.getFullYear(), hoy.getMonth(), 1)) return;
    setMes(nuevo.getMonth());
    setAnio(nuevo.getFullYear());
  };

  // El mes completo, con los huecos del principio para que caiga en su columna.
  const huecos = new Date(anio, mes, 1).getDay();
  const cantidadDeDias = new Date(anio, mes + 1, 0).getDate();
  const dias = Array.from({ length: cantidadDeDias }, (_, i) => {
    const fecha = new Date(anio, mes, i + 1);
    const clave = claveFecha(fecha);
    return { clave, numero: i + 1, esHoy: clave === claveHoy, esPasado: clave < claveHoy };
  });

  const estaElegida = (clave) => {
    if (modo === 'sueltos') return fechas.includes(clave);
    if (!rango.desde) return false;
    if (!rango.hasta) return clave === rango.desde;
    return clave >= rango.desde && clave <= rango.hasta;
  };

  const elegir = (dia) => {
    if (dia.esPasado) return;

    if (modo === 'sueltos') {
      onCambiarFechas(
        fechas.includes(dia.clave)
          ? fechas.filter((f) => f !== dia.clave)
          : [...fechas, dia.clave].sort()
      );
      return;
    }

    // En modo rango: sin inicio, o con el rango ya cerrado, arranca uno nuevo.
    // Si el segundo clic cae antes del inicio, ese pasa a ser el inicio en vez
    // de quedar en un rango invertido.
    if (!rango.desde || rango.hasta) {
      onCambiarRango({ desde: dia.clave, hasta: '' });
    } else if (dia.clave < rango.desde) {
      onCambiarRango({ desde: dia.clave, hasta: rango.desde });
    } else {
      onCambiarRango({ desde: rango.desde, hasta: dia.clave });
    }
  };

  return (
    <div className="selector-fechas">
      <div className="selector-fechas__barra">
        <button
          type="button"
          onClick={() => mover(-1)}
          disabled={enElMesActual}
          aria-label="Mes anterior"
        >
          <ChevronLeft size={18} aria-hidden="true" />
        </button>

        <span>
          {MESES[mes]} {anio}
        </span>

        <button type="button" onClick={() => mover(1)} aria-label="Mes siguiente">
          <ChevronRight size={18} aria-hidden="true" />
        </button>
      </div>

      <div className="selector-fechas__grilla">
        {DIAS.map((dia) => (
          <span key={dia} className="selector-fechas__encabezado">
            {dia}
          </span>
        ))}

        {Array.from({ length: huecos }, (_, i) => (
          <span key={`hueco-${i}`} />
        ))}

        {dias.map((dia) => {
          const elegida = estaElegida(dia.clave);
          // Relleno lleno para lo que es un extremo: en días sueltos todos lo
          // son, en un rango sólo el primero y el último. El del medio va con el
          // relleno suave, que es lo que hace legible de dónde a dónde va.
          const esExtremo =
            modo === 'sueltos' ||
            dia.clave === rango.desde ||
            dia.clave === rango.hasta;

          return (
            <button
              key={dia.clave}
              type="button"
              className={`selector-fechas__dia ${elegida ? 'es-elegida' : ''} ${
                elegida && esExtremo ? 'es-extremo' : ''
              } ${dia.esHoy ? 'es-hoy' : ''}`}
              onClick={() => elegir(dia)}
              disabled={dia.esPasado}
              aria-pressed={elegida}
            >
              {dia.numero}
            </button>
          );
        })}
      </div>

      <p className="selector-fechas__pista">
        {modo === 'sueltos'
          ? fechas.length === 0
            ? 'Tocá los días que querés bloquear'
            : `${fechas.length} ${fechas.length === 1 ? 'día elegido' : 'días elegidos'}`
          : !rango.desde
          ? 'Tocá el primer día del período'
          : !rango.hasta
          ? 'Ahora tocá el último día'
          : `Del ${formatearFechaCorta(rango.desde)} al ${formatearFechaCorta(rango.hasta)}`}
      </p>
    </div>
  );
}
